import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Boxes, Loader2, Pencil, Plus } from "lucide-react";
import KitEditor from "./KitEditor";

const split = (t) => [...new Set((t || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];

function KitApply({ kit, dc, onApplied }) {
  const [ser, setSer] = useState({});
  const [busy, setBusy] = useState(false);
  const over = kit.items.filter((it) => it.requires_serial && split(ser[it.product_id]).length > it.qty);
  const apply = async () => {
    setBusy(true);
    const serials = Object.fromEntries(Object.entries(ser).map(([k, v]) => [k, split(v)]).filter(([, v]) => v.length));
    try { await api.post(`/dc-kits/${kit.id}/apply/${dc.id}`, { serials }); toast.success(`Kit "${kit.name}" added to the DC`); onApplied(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add kit"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded border border-slate-200 bg-white p-2.5 space-y-2" data-testid={`dc-kit-apply-${kit.id}`}>
      {kit.items.map((it) => (
        <div key={it.product_id} className="text-[12px]">
          <div className="flex items-center gap-2"><span className="font-mono text-[11px] text-slate-500">{it.qty}×</span><span className="min-w-0 truncate">{it.product_name}</span>
            {it.requires_serial && <span className={`ml-auto shrink-0 text-[10px] ${split(ser[it.product_id]).length > it.qty ? "font-semibold text-rose-700" : "text-slate-500"}`} data-testid={`dc-kit-serial-count-${it.product_id}`}>{split(ser[it.product_id]).length}/{it.qty} serial(s){split(ser[it.product_id]).length > it.qty ? " — too many" : ""}</span>}</div>
          {it.requires_serial && (
            <Textarea rows={1} value={ser[it.product_id] || ""} onChange={(e) => setSer({ ...ser, [it.product_id]: e.target.value })}
              placeholder={`Scan ${it.qty > 1 ? `up to ${it.qty} serials` : "the serial"} (optional — can be added later)`} className="mt-1 min-h-[2rem] font-mono text-[12px]" data-testid={`dc-kit-serials-${it.product_id}`} />
          )}
        </div>
      ))}
      <div className="flex justify-end"><Button size="sm" className="h-8 text-[12px]" disabled={busy || over.length > 0} onClick={apply} data-testid="dc-kit-apply-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Boxes className="h-3.5 w-3.5 mr-1" />}Add kit to DC</Button></div>
    </div>
  );
}

export default function DcKitsTab({ dc, onApplied }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/dc-kits").then(({ data: d }) => setData(d)).catch(() => setData({ kits: [], can_edit: false })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-[12px] text-slate-500"><Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1" />Loading kits…</div>;
  if (edit) return <KitEditor kit={edit === "new" ? null : edit} onDone={(changed) => { setEdit(null); if (changed) { setSel(null); load(); } }} />;
  const kit = data.kits.find((k) => k.id === sel);
  return (
    <div className="space-y-2" data-testid="dc-kits-tab">
      <div className="flex flex-wrap gap-1.5">
        {data.kits.map((k) => (
          <button key={k.id} type="button" onClick={() => setSel(sel === k.id ? null : k.id)} data-testid={`dc-kit-${k.id}`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] transition-colors ${sel === k.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <Boxes className="h-3.5 w-3.5" />{k.name}<span className={`text-[10px] ${sel === k.id ? "text-slate-300" : "text-slate-400"}`}>{k.items.length} item(s)</span>
          </button>
        ))}
        {!data.kits.length && <span className="text-[12px] text-slate-500">No kits yet{data.can_edit ? " — create one for bundles you ship often" : ""}.</span>}
        {data.can_edit && <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => setEdit("new")} data-testid="dc-kit-new"><Plus className="h-3 w-3 mr-1" />New kit</Button>}
        {data.can_edit && kit && <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setEdit(kit)} data-testid="dc-kit-edit"><Pencil className="h-3 w-3 mr-1" />Edit kit</Button>}
      </div>
      {kit && <KitApply key={kit.id} kit={kit} dc={dc} onApplied={onApplied} />}
    </div>
  );
}
